import { useImperativeHandle, useMemo, useRef, useState } from "react";

import { SelectChangeEvent } from "@mui/material";

import { UseMultiselectParams } from "./Multiselect.types";

export const useMultiselect = <V, R>({
  ref,
  options,
  defaultValue,
  value,
  onChange,
}: UseMultiselectParams<V, R>) => {
  const multiselectRef = useRef<HTMLElement | null>(null);
  const [state, setState] = useState<Array<V>>(
    (value ?? defaultValue ?? []) as unknown as Array<V>
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useImperativeHandle(
    ref,
    (() => {
      return {
        instance: multiselectRef.current,
        value: state,
        setState: (v: Array<V>) => {
          setState(v);
        },
        reset: () => {
          setState([] as unknown as Array<V>);
        },
      };
    }) as unknown as () => R,
    [state]
  );

  const onChangeHandler = (event: SelectChangeEvent<Array<V>>) => {
    const newValue = event.target.value as unknown as Array<V>;
    if (typeof value === "undefined") {
      setState(newValue);
    }
    onChange?.(newValue);
  };

  const onClearStateHandler = () => {
    if (typeof value === "undefined") {
      setState([]);
    }
    onChange?.([]);
  };

  const onSelectAllHandler = (checked: boolean) => {
    const newValue = checked ? options.map((i) => i.value) : [];
    if (typeof value === "undefined") {
      setState(newValue);
    }
    onChange?.(newValue);
  };

  const valueState = useMemo(
    () => (typeof value !== "undefined" ? value : state),
    [value, state]
  );

  return {
    multiselectRef,
    valueState,
    onChangeHandler,
    onClearStateHandler,
    onSelectAllHandler,
  };
};
